import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import config from "../../../config";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  Select,
  MenuItem,
  Chip,
  Divider,
  Grid,
  Card,
  CardContent
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import RefreshIcon from "@mui/icons-material/Refresh";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

const BATCH_JOBS = [
    { 
        id: "availability_sync", 
        name: "Inventory Availability Sync", 
        description: "Scrapes product URLs for all active stores to sync their current availability status.",
        icon: "inventory_2",
        color: "#f43f5e"
    },
    { 
        id: "authorize_reconciliation", 
        name: "Authorize.net Reconciliation", 
        description: "Processes daily renewals, handles failed payments, and manages grace periods.",
        icon: "account_balance_wallet",
        color: "#8b5cf6"
    }
];

const SCHEDULE_OPTIONS = [
    { label: "15 Minutes", value: 0.25 },
    { label: "1 Hour", value: 1 },
    { label: "4 Hours", value: 4 },
    { label: "12 Hours", value: 12 },
    { label: "24 Hours", value: 24 }
];

const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString('en-US', { 
        month: 'short', 
        day: '2-digit', 
        hour: '2-digit', 
        minute: '2-digit',
        second: '2-digit'
    });
};

const parseJobDetails = (raw) => {
    try {
        let parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (typeof parsed === 'string') parsed = JSON.parse(parsed); // Double-parse in case of nested string
        return parsed || {};
    } catch (e) {
        return {};
    }
};

const LogDetailPanel = ({ log }) => {
    const details = parseJobDetails(log.details);
    const isSync = log.job_name === 'availability_sync';
    const trace = details.activity_trace || [];

    return (
        <Box sx={{ margin: 2, p: 3, backgroundColor: "#ffffff", borderRadius: 2, border: "1px solid #e2e8f0" }}>
            
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="overline" sx={{ fontWeight: 800, color: "#64748b" }}>
                    Execution Summary
                </Typography>
                <Typography variant="caption" sx={{ fontFamily: "monospace", color: "#94a3b8" }}>
                    ID: {log.id}
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ mb: 4 }}>
                {isSync ? (
                    <>
                        <Grid item xs={12} md={6}>
                            <Box sx={{ borderLeft: "3px solid #64748b", pl: 2 }}>
                                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, display: "block", mb: 0.5 }}>STORES PROCESSED</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a" }}>{details.stores_count || 0}</Typography>
                                <Typography variant="caption" sx={{ color: "#94a3b8" }}>Total store endpoints analyzed</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box sx={{ borderLeft: "3px solid #3b82f6", pl: 2 }}>
                                <Typography variant="caption" sx={{ color: "#3b82f6", fontWeight: 700, display: "block", mb: 0.5 }}>ITEMS UPDATED</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a" }}>{details.updated || 0}</Typography>
                                <Typography variant="caption" sx={{ color: "#94a3b8" }}>Availability state changes applied</Typography>
                            </Box>
                        </Grid>
                    </>
                ) : (
                    <>
                        <Grid item xs={12} md={4}>
                            <Box sx={{ borderLeft: "3px solid #10b981", pl: 2 }}>
                                <Typography variant="caption" sx={{ color: "#10b981", fontWeight: 700, display: "block", mb: 0.5 }}>PAYMENTS RECOVERED</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a" }}>{details.missed_renewals_recovered?.length || 0}</Typography>
                                <Typography variant="caption" sx={{ color: "#94a3b8" }}>Missed gateway webhooks synced</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box sx={{ borderLeft: "3px solid #f59e0b", pl: 2 }}>
                                <Typography variant="caption" sx={{ color: "#f59e0b", fontWeight: 700, display: "block", mb: 0.5 }}>WARNINGS SENT</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a" }}>{details.grace_period_warnings_sent?.length || 0}</Typography>
                                <Typography variant="caption" sx={{ color: "#94a3b8" }}>Emails sent for failed payments</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box sx={{ borderLeft: "3px solid #ef4444", pl: 2 }}>
                                <Typography variant="caption" sx={{ color: "#ef4444", fontWeight: 700, display: "block", mb: 0.5 }}>SUSPENSIONS</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a" }}>{details.subscriptions_suspended?.length || 0}</Typography>
                                <Typography variant="caption" sx={{ color: "#94a3b8" }}>Accounts suspended for failed payments</Typography>
                            </Box>
                        </Grid>
                    </>
                )}
            </Grid>

            <Divider sx={{ mb: 3 }} />

            <Typography variant="overline" sx={{ fontWeight: 800, color: "#64748b", mb: 2, display: "block" }}>
                Activity Trace
            </Typography>
            
            <Box sx={{ 
                p: 2, 
                backgroundColor: "#1e293b", 
                borderRadius: 2, 
                minHeight: "150px", 
                maxHeight: "350px", 
                overflowY: "auto",
                border: "1px solid #0f172a"
            }}>
                {trace.length === 0 ? (
                    <Typography variant="body2" sx={{ color: "#94a3b8", fontStyle: "italic" }}>No trace data recorded.</Typography>
                ) : (
                    trace.map((line, idx) => (
                        <Box key={idx} sx={{ display: "flex", gap: 1.5, mb: 1.5, alignItems: "flex-start" }}>
                            <Typography variant="caption" sx={{ color: "#475569", minWidth: "25px", fontWeight: "bold" }}>
                                {String(idx + 1).padStart(2, '0')}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#e2e8f0", fontFamily: "'IBM Plex Mono', monospace", fontSize: "0.8rem", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
                                {line}
                            </Typography>
                        </Box>
                    ))
                )}
                
                {log.error_message && (
                    <Box sx={{ mt: 3, p: 2, backgroundColor: "#7f1d1d", borderRadius: 1, border: "1px solid #991b1b" }}>
                        <Typography variant="caption" sx={{ color: "#fca5a5", fontWeight: 700, display: "block", mb: 1 }}>SYSTEM FAILURE DETECTED:</Typography>
                        <Typography variant="body2" sx={{ color: "#fecaca", fontFamily: "monospace", fontSize: "0.75rem", whiteSpace: "pre-wrap" }}>
                            {log.error_message}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

const Row = ({ log }) => {
    const [open, setOpen] = useState(false);
    const details = parseJobDetails(log.details);
    const triggerType = details.trigger_type || "automatic";
    const isManual = triggerType === "manual";

    return (
        <React.Fragment>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' }, '&:hover': { backgroundColor: '#f8fafc' } }}>
                <TableCell sx={{ width: 50 }}>
                    <IconButton size="small" onClick={() => setOpen(!open)}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e293b" }}>
                        {log.job_name === 'availability_sync' ? 'Inventory Sync' : 'Reconciliation'}
                    </Typography>
                </TableCell>
                <TableCell>
                    <Chip label={isManual ? "Manual" : "System"} size="small" sx={{ 
                        backgroundColor: isManual ? "#e0f2fe" : "#f1f5f9",
                        color: isManual ? "#0369a1" : "#475569",
                        fontWeight: 600, borderRadius: "6px"
                    }} />
                </TableCell>
                <TableCell>
                    <Chip label={log.status.toUpperCase()} size="small" sx={{ 
                        backgroundColor: log.status === 'completed' ? '#dcfce7' : log.status === 'running' ? '#fef9c3' : '#fee2e2',
                        color: log.status === 'completed' ? '#166534' : log.status === 'running' ? '#854d0e' : '#991b1b',
                        fontWeight: 700, fontSize: '0.7rem'
                    }} />
                </TableCell>
                <TableCell sx={{ color: "#64748b", fontSize: "0.85rem" }}>{formatDate(log.started_at)}</TableCell>
                <TableCell sx={{ color: "#64748b", fontSize: "0.85rem", fontFamily: "monospace" }}>
                    {log.duration_seconds ? `${Number(log.duration_seconds).toFixed(1)}s` : "--"}
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <LogDetailPanel log={log} />
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
};

const BatchJobs = () => {
    const [configs, setConfigs] = useState({});
    const [logs, setLogs] = useState([]);
    const [loadingTasks, setLoadingTasks] = useState({});
    const [pageLoading, setPageLoading] = useState(true);
    const [message, setMessage] = useState({ text: "", type: "" });

    const fetchData = async () => {
        try {
            const [configRes, logsRes] = await Promise.all([
                axios.get(`${config.CLIENT_API_BASE_URL}/admin-jobs/config`),
                axios.get(`${config.CLIENT_API_BASE_URL}/admin-jobs/logs?limit=5`)
            ]);
            setConfigs(configRes.data);
            setLogs(logsRes.data);
        } catch (err) {
            console.error("Failed to fetch jobs data", err);
        } finally {
            setPageLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Polling logic: if any job in the logs list is 'running', check every 3 seconds
    useEffect(() => {
        let interval;
        if (logs.some(log => log.status === 'running')) {
            interval = setInterval(fetchData, 3000);
        }
        return () => {
            if (interval) clearInterval(interval);
        }
    }, [logs]);

    const isJobRunning = (jobId) => logs.some(log => log.job_name === jobId && log.status === 'running');

    const handleTrigger = async (jobId) => {
        setLoadingTasks(prev => ({ ...prev, [jobId]: true }));
        setMessage({ text: "", type: "" });
        try {
            await axios.post(`${config.CLIENT_API_BASE_URL}/admin-jobs/trigger/${jobId}`, {}, { 
                headers: { "x-admin-secret": config.CRON_SECRET } 
            });
            setMessage({ type: "success", text: `${jobId} triggered successfully.` });
            setTimeout(fetchData, 2000);
        } catch (err) {
            setMessage({ type: "error", text: "Failed to trigger job." });
        } finally {
            setLoadingTasks(prev => ({ ...prev, [jobId]: false }));
        }
    };

    const handleUpdateSchedule = async (jobId, value) => {
        try {
            await axios.put(`${config.CLIENT_API_BASE_URL}/admin-jobs/config/${jobId}`, 
                { interval_value: value },
                { headers: { "x-admin-secret": config.CRON_SECRET } }
            );
            setConfigs(prev => ({ ...prev, [jobId]: { ...prev[jobId], interval_value: value } }));
            setMessage({ type: "success", text: "Schedule updated." });
        } catch (err) {
            setMessage({ type: "error", text: "Failed to update schedule." });
        }
    };

    if (pageLoading) return <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}><CircularProgress /></Box>;

    return (
        <Box sx={{ p: 4, backgroundColor: "#f8fafc", minHeight: "100vh" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 4 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: "#0f172a", mb: 0.5, letterSpacing: "-1px" }}>Batch Jobs</Typography>
                    <Typography variant="body2" color="text.secondary">Run on-demand infrastructure tasks across the platform</Typography>
                </Box>
                <Button component={Link} to="/job-logs" variant="outlined" endIcon={<ChevronRightIcon />} sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 600 }}>
                    View All Logs
                </Button>
            </Box>

            <Typography variant="overline" sx={{ fontWeight: 800, color: "#475569", mb: 2, display: "block" }}>Run New Job</Typography>
            <Grid container spacing={3} sx={{ mb: 6 }}>
                {BATCH_JOBS.map((job) => (
                    <Grid item xs={12} md={6} key={job.id}>
                        <Card sx={{ borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "none", p: 1 }}>
                            <CardContent>
                                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                                    <Box sx={{ p: 1.5, borderRadius: "11px", backgroundColor: `${job.color}15`, mr: 2 }}>
                                        <i className="material-symbols-outlined" style={{ color: job.color }}>{job.icon}</i>
                                    </Box>
                                    <Box>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{job.name}</Typography>
                                        <Typography variant="caption" color="text.secondary">{job.id}</Typography>
                                    </Box>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, height: "40px" }}>{job.description}</Typography>
                                
                                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                                    <Box sx={{ flexGrow: 1 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.5, display: "block" }}>Schedule</Typography>
                                        <Select fullWidth size="small" value={configs[job.id]?.interval_value || 24} onChange={(e) => handleUpdateSchedule(job.id, e.target.value)} sx={{ borderRadius: "8px" }}>
                                            {SCHEDULE_OPTIONS.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                                        </Select>
                                    </Box>
                                    <Box sx={{ pt: 2.5 }}>
                                        <Button variant="contained" disableElevation startIcon={loadingTasks[job.id] || isJobRunning(job.id) ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />} 
                                            onClick={() => handleTrigger(job.id)} disabled={loadingTasks[job.id] || Object.values(loadingTasks).some(Boolean) || logs.some(l => l.status === 'running')} 
                                            sx={{ borderRadius: "8px", textTransform: "none", py: 1, px: 3, backgroundColor: "#0f172a", '&:hover': { backgroundColor: "#1e293b" } }}>
                                            {loadingTasks[job.id] || isJobRunning(job.id) ? "Running..." : "Run"}
                                        </Button>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#1e293b", display: "flex", alignItems: "center" }}>
                    <AccessTimeIcon sx={{ mr: 1, fontSize: "1.2rem" }} /> Recent Jobs
                </Typography>
                <IconButton size="small" onClick={fetchData}><RefreshIcon sx={{ fontSize: "1.1rem" }} /></IconButton>
            </Box>

            <TableContainer component={Paper} sx={{ borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "none" }}>
                <Table>
                    <TableHead sx={{ backgroundColor: "#f8fafc" }}>
                        <TableRow>
                            <TableCell />
                            <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Origin</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Started</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Duration</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {logs.length === 0 ? <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6, color: "#94a3b8" }}>No recent history.</TableCell></TableRow> : 
                        logs.map((log) => <Row key={log.id} log={log} />)}
                    </TableBody>
                </Table>
            </TableContainer>

            <Snackbar 
                open={!!message.text} 
                autoHideDuration={4000} 
                onClose={() => setMessage({ text: "", type: "" })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setMessage({ text: "", type: "" })} severity={message.type || 'info'} sx={{ width: '100%', fontWeight: 600 }}>
                    {message.text}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default BatchJobs;
